import React, {useEffect, useRef, useState} from 'react';
import {getUser} from "../../../../common/util/util-auth";
import {classNames} from "../../../../common/util/util-helpers";
import XMarkIcon from "@heroicons/react/20/solid/XMarkIcon";
import Button from "../../../../common/components/button";
import PlusIcon from "@heroicons/react/20/solid/PlusIcon";
import Tooltip from "../../../../common/components/tooltip";
import ModalDefault from "../../../../common/components/modal/modal-default";
import {QRCodeCanvas} from "qrcode.react";
import FieldsForm from "../../../../common/components/fields/fields-form";
import {Field, FieldsManager} from "../../../../data/services/fields";
import {useDispatch} from "react-redux";
import axios from "axios";
import Env from "../../../../util/env";
import {LoaderLarge, LoaderSmall} from "../../../../common/components/loader";
import {pushNotification} from "../../../../data/actions/ui";
import InfoBar from "../../../../common/components/info-paragraph/info-bar";
import InformationCircleIcon from "@heroicons/react/24/outline/InformationCircleIcon";

export default function TwoFactorMethods({contact, translate, twoFactorAuthOptionsRef, onSubmit, isDirty, onSetDirty}) {
    const email = getUser('Contact.Email');
    const dispatch = useDispatch();
    const isFirstTimeMounted = useRef(true);
    const [options, setOptions] = useState(() => getOptions());
    const [isAddMethodDialogOpen, setIsAddMethodDialogOpen] = useState(false);
    const [fields, setFields] = useState(() => getFields());
    const [QRSVG, setQRSVG] = useState("");
    const [secret, setSecret] = useState("");
    const [isSVGLoading, setIsSVGLoading] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [error, setError] = useState("");

    function otpGenerate() {
        const token = getUser()?.access_token;
        setIsSVGLoading(true);

        return axios.post(
            Env.getApiUrl('otp/generate'),
            {},
            {
                headers: token ? {
                    'Authorization': 'Bearer ' + token
                } : {}
            }
        )
            .then((response) => {
                setIsSVGLoading(false);
                setQRSVG(response?.data?.data?.QRSVG);
                setSecret(response?.data?.data?.Secret);
            })
            .catch(() => {
                dispatch(pushNotification({
                        title: "Error connecting to the server",
                        messageType: "error"
                    }
                ));
                setIsAddMethodDialogOpen(false);
            });
    }

    function handleVerifyClick() {
        const token = getUser()?.access_token;
        setIsVerifying(true);
        return axios.post(
            Env.getApiUrl('otp/verify'),
            {
                "Code": fields.Code.value,
                "Secret": secret
            },
            {
                headers: token ? {
                    'Authorization': 'Bearer ' + token
                } : {}
            }
        )
            .then((response) => {
                if (response?.data?.data.Secret) {
                    const optionsUpdate = Object.assign({}, options);
                    optionsUpdate.TwoFactorAuthTypeAuthy.enabled = 1;
                    setOptions(optionsUpdate);
                    setIsAddMethodDialogOpen(false);
                    dispatch(pushNotification({
                            title: `Authenticator added successfully`,
                            messageType: "success"
                        }
                    ));
                    onSubmit();
                }
            })
            .catch((error) => {
                setError(error?.response?.data?.message);
                setIsSVGLoading(false)
                setIsVerifying(false);
            });
    }

    function getOptions() {
        return {
            "TwoFactorAuthTypeEmail": {
                name: "TwoFactorAuthTypeEmail",
                info: email,
                TwoFactorAuthType: 1,
                selected: !contact.TwoFactorAuthType || contact.TwoFactorAuthType === 1,
                enabled: 1
            },
            "TwoFactorAuthTypeSMS": {
                name: "TwoFactorAuthTypeSMS",
                info: "", // user phone number
                TwoFactorAuthType: 2,
                selected: contact.TwoFactorAuthType === 2,
                enabled: contact.TwoFactorAuthTypeSMS ? 1 : 0
            },
            "TwoFactorAuthTypeAuthy": {
                name: "TwoFactorAuthTypeAuthy",
                info: "",
                TwoFactorAuthType: 3,
                selected: contact.TwoFactorAuthType === 3,
                enabled: contact.TwoFactorAuthTypeAuthy ? 1 : 0
            }
        }
    }

    function handleRemoveTypeClick(it) {
        const optionsClone = Object.assign({}, options);
        optionsClone[it.name].enabled = 0;
        setOptions(optionsClone);
    }

    function handleInputChange(name, value) {
        fields[name].errorMessage = "";
        setFields(FieldsManager.updateField(fields, name, value))
    }

    function handleAuthTypeChange(name) {
        const optionsClone = Object.values(options).reduce((memo, it) => {
            it.selected = it.name === name;
            memo[it.name] = it;
            return memo;
        }, {});

        setOptions(optionsClone);
    }

    function getFields() {
        return {
            Code: new Field('Code', '', [''], false, 'text', {
                max: 6,
                placeholder: "XXXXXX",
                hideLabel: true,
                addClass: "text-center"
            })
        }
    }

    useEffect(() => {
        if (isAddMethodDialogOpen) {
            otpGenerate();
            setFields(getFields());
            setError("");
        }
    }, [isAddMethodDialogOpen])


    useEffect(() => {
        twoFactorAuthOptionsRef.current = options;

        if (!isFirstTimeMounted.current) {
            onSetDirty();
        } else {
            isFirstTimeMounted.current = false;
        }
    }, [options]);

    return <div className="text-base flex flex-col gap-4 border-t-2 border-tm-gray-200 pt-4">
        {Object.values(options).filter(it => it.enabled).map(it => {
            return <div key={it.TwoFactorAuthType} className="flex gap-2 items-center group">
                <label
                    className={
                        classNames(
                            "relative flex gap-2 items-center px-4 py-1 rounded-btn -ml-4 cursor-pointer flex-1",
                            it.selected ? "bg-primary/10 -mr-4" : "hover:bg-tm-gray-100"
                        )}
                >
                    <input type="radio" onChange={() => handleAuthTypeChange(it.name)} value={it.TwoFactorAuthType}
                           name="TwoFactorAuthType" checked={it.selected}/>
                    <div className="">{translate("field." + it.name)} {it.info ?
                        <span className="text-tm-gray-600"> - {it.info}</span> : undefined}</div>

                    {!!it.selected && (
                        <div
                            className="absolute text-[0.6rem] font-black text-primary border-3 rounded-btn px-2 py-0.5 border-inverse uppercase right-4">
                            Primary
                        </div>
                    )}
                </label>

                {(!it.selected && it.name !== "TwoFactorAuthTypeEmail") && (
                    <Tooltip content={translate("text.remove_auth_type")}>
                        <button
                            onClick={() => handleRemoveTypeClick(it)}
                            className={
                                classNames(
                                    "cursor-pointer bg-red-50 hover:bg-red-100 text-red-700 rounded-btn size-8 flex items-center justify-center opacity-0 outline-none group-hover:opacity-100 focus:ring-2 focus:ring-red-500"
                                )
                            }
                        >
                            <XMarkIcon className="size-5"/>
                        </button>
                    </Tooltip>
                )}
            </div>
        })}

        {!options.TwoFactorAuthTypeAuthy.enabled && (
            <div className="text-right border-t-2 border-tm-gray-200 pt-4">
                <Button
                    disabled={isDirty}
                    onClick={() => setIsAddMethodDialogOpen(true)}
                >
                    <PlusIcon className="size-5 mr-2 -ml-1"/> Add authenticator app (Authy, Google Authenticator, Microsfot Authenticator, FreeOTP)
                </Button>

                {isDirty && (
                    <Button addClass="p-2 ml-1" toolTip={translate("text.save_changes_first")}>
                        <InformationCircleIcon className="size-5"/>
                    </Button>
                )}
            </div>
        )}

        <ModalDefault
            show={isAddMethodDialogOpen}
            widthClass={"max-w-lg"}
            title={translate("text.set_up_authenticator")}
            closeButtonLabel={translate("btn.close")}
            onClose={() => {
                setIsAddMethodDialogOpen(false);
            }}
        >
            <div className="p-6 text-center">
                <p className="font-bold text-base text-tm-gray-700 mb-4">Scan the QR code using your authenticator
                    app</p>

                <p>You can use any RFC 6238-compliant app such as <span
                    className="font-bold">Google Authenticator</span>, <span
                    className="font-bold">Microsoft Authenticator</span>,
                    or <span className="font-bold">Authy</span> to scan the QR code and generate your verification
                    codes.</p>

                <div className="flex justify-center py-4">
                    <div className="border-4 border-tm-gray-300">
                        {isSVGLoading && (
                            <div className="size-[150px] relative">
                                <LoaderLarge/>
                            </div>
                        )}

                        {!isSVGLoading && QRSVG && (
                            <QRCodeCanvas
                                value={QRSVG}
                                size={200}
                                bgColor="#ffffff"
                                fgColor="#000000"
                                level="H"
                                includeMargin={true}
                            />
                        )}
                    </div>
                </div>

                <p>Enter 6 digit code from your Authenticator app to complete setup.</p>

                <div className="max-w-xs space-y-6 mx-auto">
                    <FieldsForm
                        fieldsState={fields}
                        onChange={handleInputChange}
                        // isFormDisabled={isLoading}
                        translate={translate}
                    />

                    {!!error && (
                        <InfoBar type="danger">
                            {translate("text." + error)}
                        </InfoBar>
                    )}

                    <div className="flex">
                        <Button
                            disabled={isVerifying || fields.Code.value.length !== 6}
                            onClick={handleVerifyClick}
                            appearance="primary"
                            addClass="w-full justify-center"
                        >
                    <span className="px-7 relative">
                        {isVerifying && (
                            <LoaderSmall addClass="absolute left-0"/>
                        )}

                        {translate('btn.verify')}
                     </span>
                        </Button>
                    </div>
                </div>
            </div>
        </ModalDefault>
    </div>
}